import React, { useCallback, useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { AnimatePresence, motion } from "framer-motion";
import { toast } from "react-toastify";
import axiosInstance from "../../utils/axios/axiosConfig";
import { Images, UploadSimple, MagnifyingGlass, X, CheckCircle, Spinner } from "@phosphor-icons/react";
import { Button, Input, cn } from "../ui";

const MAX_BYTES = 15 * 1024 * 1024;

const readFileAsDataUri = (file) =>
  new Promise((resolve, reject) => {
    const reader = new FileReader();
    reader.onload = () => resolve(reader.result);
    reader.onerror = reject;
    reader.readAsDataURL(file);
  });

/**
 * MediaPicker — reusable media-library modal for the whole panel.
 *
 * Two tabs: pick from the library, or upload a new image (stored in the
 * shared `media` table + CDN). The modal persists until the user inserts or
 * closes it — metadata (alt / caption / title) is edited inline before
 * inserting.
 *
 * Props:
 *  - open, onClose
 *  - onSelect({ url, alt, caption, title, media })  — called on insert
 *  - withMeta   (default true)  — show alt/caption/title fields before insert
 *  - insertLabel (default "Insérer")
 */
export default function MediaPicker({ open, onClose, onSelect, withMeta = true, insertLabel = "Insérer" }) {
  const [tab, setTab] = useState("library"); // library | upload
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [lastPage, setLastPage] = useState(1);
  const [selected, setSelected] = useState(null);

  // metadata attached to the image being inserted
  const [meta, setMeta] = useState({ alt: "", caption: "", title: "" });

  // upload tab state
  const [file, setFile] = useState(null); // { dataUri, name }
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);

  const fetchMedia = useCallback(
    async (p = 1, q = "") => {
      try {
        setLoading(true);
        const { data } = await axiosInstance.post(`/media/search?page=${p}&per_page=24`, { searchValue: q });
        setItems(p === 1 ? data.data || [] : (prev) => [...prev, ...(data.data || [])]);
        setPage(data.current_page || p);
        setLastPage(data.last_page || 1);
      } catch (error) {
        toast.error(error?.response?.data?.message || "Impossible de charger la médiathèque");
      } finally {
        setLoading(false);
      }
    },
    []
  );

  // load / reset when opening
  useEffect(() => {
    if (!open) return;
    setTab("library");
    setSelected(null);
    setFile(null);
    setMeta({ alt: "", caption: "", title: "" });
    setSearch("");
    fetchMedia(1, "");
  }, [open, fetchMedia]);

  // debounced search
  useEffect(() => {
    if (!open) return;
    const t = setTimeout(() => fetchMedia(1, search), 350);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [search]);

  useEffect(() => {
    const onKey = (e) => open && e.key === "Escape" && onClose?.();
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open, onClose]);

  const pick = (m) => {
    setSelected(m);
    // prefill insertion metadata from the library entry
    setMeta({ alt: m.alt || "", caption: m.caption || "", title: m.title || "" });
  };

  const handleFiles = async (f) => {
    if (!f) return;
    if (!f.type.startsWith("image/")) return toast.warning("Seules les images sont acceptées");
    if (f.size > MAX_BYTES) return toast.warning("L'image dépasse la limite de 15 Mo");
    const dataUri = await readFileAsDataUri(f);
    setFile({ dataUri, name: f.name });
  };

  const insertFromLibrary = () => {
    if (!selected) return toast.info("Sélectionnez une image d'abord");
    onSelect?.({ url: selected.url, ...meta, media: selected });
    onClose?.();
  };

  const uploadAndInsert = async () => {
    if (!file) return toast.info("Choisissez un fichier d'abord");
    try {
      setUploading(true);
      const { data } = await axiosInstance.post("/media/store", {
        image: file.dataUri,
        name: file.name,
        alt: meta.alt || null,
        caption: meta.caption || null,
        title: meta.title || null,
      });
      const m = data?.data;
      toast.success("Image ajoutée à la médiathèque");
      onSelect?.({ url: m.url, alt: m.alt || "", caption: m.caption || "", title: m.title || "", media: m });
      onClose?.();
    } catch (error) {
      toast.error(error?.response?.data?.message || "Échec de l'upload");
    } finally {
      setUploading(false);
    }
  };

  const Tab = ({ id, icon: Icon, children }) => (
    <button
      type="button"
      onClick={() => setTab(id)}
      className={cn(
        "inline-flex items-center gap-1.5 rounded-lg px-3 py-1.5 text-xs font-medium transition",
        tab === id ? "bg-accent text-white" : "text-muted hover:bg-surface-2 hover:text-ink"
      )}
    >
      <Icon size={15} /> {children}
    </button>
  );

  const MetaFields = () => (
    <div className="grid grid-cols-1 gap-3 sm:grid-cols-3">
      <Input
        label="Texte alternatif (alt)"
        value={meta.alt}
        onChange={(e) => setMeta((m) => ({ ...m, alt: e.target.value }))}
        placeholder="Recommandé — SEO & accessibilité"
      />
      <Input
        label="Légende (facultatif)"
        value={meta.caption}
        onChange={(e) => setMeta((m) => ({ ...m, caption: e.target.value }))}
        placeholder="Affichée sous l'image"
      />
      <Input
        label="Titre (facultatif)"
        value={meta.title}
        onChange={(e) => setMeta((m) => ({ ...m, title: e.target.value }))}
        placeholder="Info-bulle au survol"
      />
    </div>
  );

  return createPortal(
    <AnimatePresence>
      {open ? (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 z-[80] flex items-center justify-center bg-black/50 p-4 backdrop-blur-sm"
          onClick={() => !uploading && onClose?.()}
        >
          <motion.div
            initial={{ opacity: 0, y: 14, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 10, scale: 0.98 }}
            transition={{ duration: 0.2, ease: [0.16, 1, 0.3, 1] }}
            className="flex max-h-[85vh] w-full max-w-3xl flex-col rounded-2xl border border-line bg-surface shadow-xl"
            onClick={(e) => e.stopPropagation()}
          >
            {/* Header */}
            <div className="flex items-center justify-between gap-3 border-b border-line px-5 py-4">
              <div className="flex items-center gap-3">
                <h3 className="text-base font-semibold text-ink">Médiathèque</h3>
                <div className="flex items-center gap-1 rounded-xl border border-line bg-surface p-1">
                  <Tab id="library" icon={Images}>Bibliothèque</Tab>
                  <Tab id="upload" icon={UploadSimple}>Uploader</Tab>
                </div>
              </div>
              <button
                type="button"
                onClick={() => !uploading && onClose?.()}
                className="grid h-8 w-8 place-items-center rounded-lg text-muted transition hover:bg-surface-2 hover:text-ink"
                aria-label="Fermer"
              >
                <X size={16} />
              </button>
            </div>

            {/* Body */}
            <div className="flex-1 overflow-y-auto p-5">
              {tab === "library" && (
                <>
                  <Input
                    icon={MagnifyingGlass}
                    placeholder="Rechercher par nom ou texte alternatif…"
                    value={search}
                    onChange={(e) => setSearch(e.target.value)}
                    containerClassName="mb-4"
                  />

                  {items.length === 0 && !loading ? (
                    <div className="grid place-items-center rounded-xl border-2 border-dashed border-line py-14 text-center">
                      <Images size={28} className="mb-2 text-faint" />
                      <p className="text-sm text-muted">Aucune image dans la médiathèque.</p>
                      <button type="button" onClick={() => setTab("upload")} className="mt-2 text-xs font-medium text-accent hover:underline">
                        Uploader la première image
                      </button>
                    </div>
                  ) : (
                    <div className="grid grid-cols-3 gap-3 sm:grid-cols-4 md:grid-cols-5">
                      {items.map((m) => (
                        <button
                          key={m.id}
                          type="button"
                          onClick={() => pick(m)}
                          className={cn(
                            "group relative aspect-square overflow-hidden rounded-xl border bg-surface-2 transition",
                            selected?.id === m.id ? "border-accent ring-2 ring-accent/40" : "border-line hover:border-accent/50"
                          )}
                          title={m.name}
                        >
                          <img src={m.url} alt={m.alt || m.name} loading="lazy" className="h-full w-full object-cover" />
                          {selected?.id === m.id && (
                            <span className="absolute right-1.5 top-1.5 rounded-full bg-white text-accent">
                              <CheckCircle size={20} weight="fill" />
                            </span>
                          )}
                        </button>
                      ))}
                    </div>
                  )}

                  {loading && (
                    <div className="mt-4 flex items-center justify-center gap-2 text-sm text-muted">
                      <Spinner size={16} className="animate-spin" /> Chargement…
                    </div>
                  )}

                  {!loading && page < lastPage && (
                    <div className="mt-4 text-center">
                      <Button type="button" variant="outline" onClick={() => fetchMedia(page + 1, search)}>
                        Charger plus
                      </Button>
                    </div>
                  )}
                </>
              )}

              {tab === "upload" && (
                <div className="space-y-4">
                  {file ? (
                    <div className="relative overflow-hidden rounded-xl border border-line">
                      <img src={file.dataUri} alt="" className="max-h-64 w-full object-contain bg-surface-2" />
                      <button
                        type="button"
                        onClick={() => { setFile(null); if (fileInputRef.current) fileInputRef.current.value = ""; }}
                        className="absolute right-2 top-2 grid h-8 w-8 place-items-center rounded-lg bg-black/60 text-white transition hover:bg-black/80"
                        title="Retirer"
                      >
                        <X size={16} />
                      </button>
                      <div className="border-t border-line bg-surface px-3 py-2 text-xs text-muted">{file.name}</div>
                    </div>
                  ) : (
                    <label
                      className="flex cursor-pointer flex-col items-center justify-center gap-2 rounded-xl border-2 border-dashed border-line bg-surface-2 px-6 py-12 text-center transition hover:border-accent/50 hover:bg-surface"
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => { e.preventDefault(); handleFiles(e.dataTransfer.files?.[0]); }}
                    >
                      <span className="grid h-11 w-11 place-items-center rounded-full border border-line bg-surface text-faint">
                        <UploadSimple size={20} />
                      </span>
                      <span className="text-sm font-medium text-ink">Glissez une image ici ou cliquez pour parcourir</span>
                      <span className="text-xs text-muted">JPG, PNG, WEBP ou GIF · max 15 Mo</span>
                      <input
                        ref={fileInputRef}
                        type="file"
                        className="hidden"
                        accept="image/jpeg,image/png,image/webp,image/gif"
                        onChange={(e) => handleFiles(e.target.files?.[0])}
                      />
                    </label>
                  )}
                </div>
              )}
            </div>

            {/* Footer — metadata + insert (persists across tab switches) */}
            <div className="space-y-4 border-t border-line px-5 py-4">
              {withMeta && MetaFields()}
              <div className="flex items-center justify-between gap-3">
                <p className="text-xs text-muted">
                  {tab === "library"
                    ? selected ? `Sélection : ${selected.name}` : "Cliquez sur une image pour la sélectionner."
                    : "L'image sera ajoutée à la médiathèque puis insérée."}
                </p>
                <div className="flex gap-2">
                  <Button type="button" variant="secondary" onClick={onClose} disabled={uploading}>
                    Annuler
                  </Button>
                  {tab === "library" ? (
                    <Button type="button" onClick={insertFromLibrary} disabled={!selected}>
                      {insertLabel}
                    </Button>
                  ) : (
                    <Button type="button" onClick={uploadAndInsert} loading={uploading} disabled={!file}>
                      Uploader & {insertLabel.toLowerCase()}
                    </Button>
                  )}
                </div>
              </div>
            </div>
          </motion.div>
        </motion.div>
      ) : null}
    </AnimatePresence>,
    document.body
  );
}
